import { useEffect, useState, useMemo, useContext, useRef } from "react";
import styles from "./purchases.module.css";
import cardTwoStyles from "../../../components/cards/cardTwo.module.css";
import { GoBackButton } from "../../../components/buttons/goBack";
import { CardOne } from "../../../components/cards/cardOne";
import { navigationGamepadLoop } from "../../../utilities/utilities";
import { themeContext } from "../../../context/theme";
import { ThemeLoader } from "../../../components/svg/themeLoader";

const API_BASE_URL =
  process.env.REACT_APP_SERVER_URL || "http://localhost:8080";


export const Purchases = ({ setShowPurchases, game }) => {
  const { theme } = useContext(themeContext);
  const [isLoading, setIsLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState(false);
  const sectionRef = useRef(null);

  const gamepadLoopState = useMemo(() => ({ run: true }), []);

  const goBack = () => {
    game.sounds.clickSound.play();
    gamepadLoopState.run = false;
    setShowPurchases(false);
  };

  const keyDownHandler = (event) => {
    if (event.key === "ArrowDown") {
      sectionRef.current?.scrollBy({ top: 80 });
    } else if (event.key === "ArrowUp") {
      sectionRef.current?.scrollBy({ top: -80 });
    } else if (event.key === "Escape" || event.key === "Back") {
      goBack();
    }
  };

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/user/purchases`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        if (data.success) setPurchases(data.purchases);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPurchases();
  }, []);

  useEffect(() => {
    sectionRef.current?.focus();
  }, []);

  useEffect(() => {
    navigationGamepadLoop(game, keyDownHandler, gamepadLoopState);
  }, []);

  useEffect(() => {
    return () => {
      gamepadLoopState.run = false;
    };
  }, []);

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const cardClass = cardTwoStyles["card-two-" + theme] + " " + styles["purchase-card"];

  return (
    <CardOne
      customTag="section"
      attributes={{
        className: styles["purchases-section"],
        onKeyDown: keyDownHandler,
        tabIndex: -1,
        ref: sectionRef,
      }}
    >
      {isLoading ? (
        <div className={styles["loader-container"]}>
          <ThemeLoader theme={theme} />
          <div className={styles["loading-text"]}>Loading purchases...</div>
        </div>
      ) : (
        <>
          <GoBackButton onClickHandler={goBack} game={game} />
          <div className={styles["purchases"]}>
            <h1 className={styles["purchases-heading"]}>Purchases</h1>
            <p className={styles["purchases-hint"]}>
              If you have made any purchase, it can take a minute or two to reflect here. Take a chill pill, play a game and check back.
            </p>

            {error ? (
              <div className={styles["list-empty"]}>
                <p className={styles["empty-text"]}>Something went wrong.</p>
              </div>
            ) : purchases.length === 0 ? (
              <div className={styles["list-empty"]}>
                <p className={styles["empty-text"]}>No purchases yet.</p>
                <p className={styles["empty-sub"]}>
                  Visit the Store to unlock premium app skins and board skins.
                </p>
              </div>
            ) : (
              <div className={styles["list"]}>
                {purchases.map((purchase) => (
                  <div key={purchase.id} className={cardClass}>
                    <div className={styles["row"]}>
                      <span className={styles["row-label"]}>date</span>
                      <div className={styles["row-right"]}>
                        {purchase.status === "REFUNDED" && (
                          <span className={styles["refund-pill"]}>refunded</span>
                        )}
                        <span className={styles["value-pill"]}>
                          {formatDate(purchase.createdAt)}
                        </span>
                      </div>
                    </div>

                    <div className={styles["row"]}>
                      <span className={styles["row-label"]}>amount</span>
                      <span className={styles["value-pill"]}>
                        ${purchase.amount.toFixed(2)} {purchase.currency.toUpperCase()}
                      </span>
                    </div>

                    <div className={styles["section-gap"]} />
                    <div className={styles["section-label"]}>unlocked</div>

                    {purchase.items.map((item) => (
                      <div key={item.id} className={styles["row"]}>
                        <span className={styles["row-label"]}>
                          {item.type === "THEME" ? "app skin" : "board skin"}
                        </span>
                        <span className={styles["value-pill"]}>{item.name}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </CardOne>
  );
};
